import React, { useState } from 'react';
import 'altcha';
import Button from '../ui/Button';
import discordIdExample from '../../assets/images/discordid.png';

export default function EnlistmentForm() {

  const [formData, setFormData] = useState({
    nickname: '',
    email: '',
    discordId: '',
    birthDate: '',
    whyJoin: '',
    howDidYouHear: '',
    personalDescription: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [altchaPayload, setAltchaPayload] = useState(null);

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nickname || formData.nickname.length < 2) {
      newErrors.nickname = 'El nickname debe tener al menos 2 caracteres';
    }

    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ingresá un email válido';
    }

    if (!formData.discordId || !/^\d{17,19}$/.test(formData.discordId)) {
      newErrors.discordId = 'Discord ID debe ser un número de 17-19 dígitos';
    }

    if (!formData.birthDate) {
      newErrors.birthDate = 'La fecha de nacimiento es obligatoria';
    } else {
      const age = calculateAge(formData.birthDate);
      if (age < 18) {
        newErrors.birthDate = 'Debes tener al menos 18 años';
      }
    }

    if (!formData.whyJoin || formData.whyJoin.length < 20) {
      newErrors.whyJoin = 'Mínimo 20 caracteres';
    }

    if (!formData.howDidYouHear || formData.howDidYouHear.length < 10) {
      newErrors.howDidYouHear = 'Mínimo 10 caracteres';
    }

    if (!formData.personalDescription || formData.personalDescription.length < 20) {
      newErrors.personalDescription = 'Mínimo 20 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!altchaPayload) {
      setErrors({ submit: 'Por favor completá la verificación de seguridad.' });
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch('https://api.clanusml.net/api/enlistment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          altchaPayload,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al enviar solicitud');
      }

      setSubmitted(true);
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  if (submitted) {
    return (
      <div className="enlistment-success">
        <div className="success-icon">✓</div>
        <h3>¡Solicitud Enviada!</h3>
        <p>Recibimos tu solicitud de enlistamiento. Un reclutador se contactará con vos en las próximas horas por Discord.</p>
        <p className="success-note">También enviamos una confirmación a tu Discord por mensaje privado.</p>
      </div>
    );
  }

  return (
    <form className="enlistment-form" onSubmit={handleSubmit}>
      <div className="form-grid">
        <div className="form-field">
          <label htmlFor="nickname">Nombre dentro de la comunidad *</label>
          <input
            id="nickname"
            type="text"
            value={formData.nickname}
            onChange={(e) => handleChange('nickname', e.target.value)}
            placeholder="Tu nickname"
            className={errors.nickname ? 'error' : ''}
          />
          {errors.nickname && <span className="field-error">{errors.nickname}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="email">Email *</label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="tu@email.com"
            className={errors.email ? 'error' : ''}
          />
          {errors.email && <span className="field-error">{errors.email}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="discordId">
            Discord ID *
            <span className="field-hint">
              (Clic derecho en tu perfil → Copiar ID de usuario) —{' '}
              <a
                href={discordIdExample}
                target="_blank"
                rel="noopener noreferrer"
                className="field-hint-link"
              >
                Ver ejemplo visual
              </a>
            </span>
          </label>
          <input
            id="discordId"
            type="text"
            value={formData.discordId}
            onChange={(e) => handleChange('discordId', e.target.value)}
            placeholder="123456789012345678"
            className={errors.discordId ? 'error' : ''}
          />
          {errors.discordId && <span className="field-error">{errors.discordId}</span>}
        </div>

        <div className="form-field">
          <label htmlFor="birthDate">Fecha de Nacimiento *</label>
          <input
            id="birthDate"
            type="date"
            value={formData.birthDate}
            onChange={(e) => handleChange('birthDate', e.target.value)}
            className={errors.birthDate ? 'error' : ''}
          />
          {errors.birthDate && <span className="field-error">{errors.birthDate}</span>}
        </div>

        <div className="form-field full-width">
          <label htmlFor="whyJoin">¿Por qué querés unirte a USML? *</label>
          <textarea
            id="whyJoin"
            value={formData.whyJoin}
            onChange={(e) => handleChange('whyJoin', e.target.value)}
            placeholder="Contanos qué te motivó a solicitar enlistamiento..."
            rows="4"
            className={errors.whyJoin ? 'error' : ''}
          />
          {errors.whyJoin && <span className="field-error">{errors.whyJoin}</span>}
        </div>

        <div className="form-field full-width">
          <label htmlFor="howDidYouHear">¿Cómo te enteraste de USML? *</label>
          <textarea
            id="howDidYouHear"
            value={formData.howDidYouHear}
            onChange={(e) => handleChange('howDidYouHear', e.target.value)}
            placeholder="YouTube, Discord, amigo, etc..."
            rows="2"
            className={errors.howDidYouHear ? 'error' : ''}
          />
          {errors.howDidYouHear && <span className="field-error">{errors.howDidYouHear}</span>}
        </div>

        <div className="form-field full-width">
          <label htmlFor="personalDescription">Breve descripción personal *</label>
          <textarea
            id="personalDescription"
            value={formData.personalDescription}
            onChange={(e) => handleChange('personalDescription', e.target.value)}
            placeholder="Contanos un poco sobre vos, tus intereses, disponibilidad horaria..."
            rows="4"
            className={errors.personalDescription ? 'error' : ''}
          />
          {errors.personalDescription && <span className="field-error">{errors.personalDescription}</span>}
        </div>
      </div>

      {errors.submit && (
        <div className="form-error">{errors.submit}</div>
      )}

      <div className="altcha-container">
        <altcha-widget
          auto="onsubmit"
          hidefooter
          hidelogo
          onStateChange={(e) => {
            if (e.detail.state === 'verified') {
              setAltchaPayload(e.detail.payload);
            }
          }}
        />
      </div>

      <div className="form-actions">
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={submitting || !altchaPayload}
          loading={submitting}
        >
          Enviar Solicitud
        </Button>
      </div>
    </form>
  );
}
